/**
 * GA4 Explore for one client: the zero-setup "browse every metric" page
 * AgencyAnalytics gives each connector. Header (view title, "Account is
 * <property>" chip, Add Filter, dates with compare, Settings, Share,
 * Presentation Mode), then the selected metric's line chart beside a donut,
 * one card per enabled metric, and the table.
 */

import { useState, type ReactNode } from 'react';

import { ChangeFormatContext, changeFormatOf } from '../../reports/change-format.ts';
import { DateControl } from '../../reports/DateControl.tsx';
import type {
  Catalogue,
  Client,
  CompareSetting,
  Filter,
  RangeSetting,
} from '../../reports/reports-api.ts';
import { hrefFor } from '../routes.ts';
import { ExploreCharts } from './ExploreCharts.tsx';
import { ExploreCards } from './ExploreCards.tsx';
import { ExploreFilters } from './ExploreFilters.tsx';
import { FilterChips } from './FilterChips.tsx';
import { ExploreSettings } from './ExploreSettings.tsx';
import { ExploreShare } from './ExploreShare.tsx';
import { ExploreTable, type TableState } from './ExploreTable.tsx';
import { CONVERSIONS_LABEL, findView, viewFilters, type DateInterval } from './ga4-views.ts';
import { useExploreData } from './use-explore-data.ts';
import { useExploreMetrics } from './use-explore-metrics.ts';

const DEFAULT_TIMEZONE = 'Africa/Johannesburg';

export function Ga4Explore(props: {
  organisationId: string;
  client: Client;
  catalogue: Catalogue;
  viewSlug: string;
  property: string | null;
  /** A Share action created a client report: refresh the client's report list. */
  onReportCreated: () => void;
}): ReactNode {
  const source = props.catalogue.sources.find((item) => item.key === 'ga4');
  const { section, view } = findView(props.viewSlug);
  const allMetrics = source?.metrics ?? [];
  const metricsState = useExploreMetrics(
    props.organisationId,
    allMetrics.map((metric) => metric.key),
  );
  const [range, setRange] = useState<RangeSetting>({ preset: 'LAST_MONTH' });
  const [compare, setCompare] = useState<CompareSetting>({ mode: 'NONE' });
  const [filters, setFilters] = useState<Filter[]>([]);
  const [interval, setDateInterval] = useState<DateInterval>('auto');
  const [grouping, setGrouping] = useState('channel');
  const [selectedByView, setSelectedByView] = useState<Record<string, string>>({});
  const [tableByView, setTableByView] = useState<Record<string, TableState>>({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [presenting, setPresenting] = useState(false);

  const dimensionLabel = (key: string) =>
    source?.dimensions.find((item) => item.key === key)?.label ?? key;
  const asGrouping = (key: string) => (key === 'channel' ? grouping : key);
  const cardKeys = view.cards ?? metricsState.enabled;
  const metricOf = (key: string) => {
    const metric = allMetrics.find((item) => item.key === key);
    return metric && view.cards
      ? { ...metric, label: CONVERSIONS_LABEL[key] ?? metric.label }
      : metric;
  };
  const cards = cardKeys.map(metricOf).filter((metric) => metric !== undefined);
  const selected = selectedByView[view.slug] ?? view.selected ?? cards[0]?.key ?? 'sessions';
  const table: TableState = tableByView[view.slug] ?? {
    dimension: view.table[0] ?? 'channel',
    search: '',
    sort: { metric: selected, direction: 'desc' },
    page: 0,
  };
  const applied = viewFilters(view, grouping, filters);
  const timezone = props.client.timezone ?? DEFAULT_TIMEZONE;
  const data = useExploreData({
    organisationId: props.organisationId,
    clientId: props.client.id,
    timezone,
    range,
    compare,
    filters: applied,
    cards: cards.map((metric) => metric.key),
    selected,
    donut: view.donut ? asGrouping(view.donut) : null,
    granularity: interval === 'auto' ? null : interval,
    table: {
      ...table,
      dimension: asGrouping(table.dimension),
      metrics: cards.map((metric) => metric.key),
    },
  });
  const selectedMetric = cards.find((metric) => metric.key === selected) ?? cards[0];
  const title = `${section.title}: ${view.title}`;

  const page = (
    <div className={`ex-page${presenting ? ' ex-present' : ''}`}>
      <header className="ex-head">
        <h1>
          <span className="ex-source">Google Analytics 4</span> {title}
        </h1>
        {!presenting && (
          <div className="ex-tools">
            <span className="ex-chip ex-account">
              Account is {props.property ?? 'not attached'}
            </span>
            <ExploreFilters
              organisationId={props.organisationId}
              clientId={props.client.id}
              timezone={timezone}
              range={range}
              dimensionLabel={dimensionLabel}
              filters={filters}
              onFilters={setFilters}
              grouping={grouping}
              onGrouping={setGrouping}
              interval={interval}
              onInterval={setDateInterval}
            />
            <DateControl
              range={range}
              onRange={setRange}
              compare={compare}
              onCompare={setCompare}
            />
            <button type="button" className="btn secondary" onClick={() => setSettingsOpen(true)}>
              Settings
            </button>
            <ExploreShare
              organisationId={props.organisationId}
              onReportCreated={props.onReportCreated}
              snapshot={{
                client: props.client,
                viewTitle: title,
                range,
                compare,
                timezone,
                filters: applied,
                cards: cards.map((metric) => metric.key),
                selected,
                donut: view.donut ? asGrouping(view.donut) : null,
                table: { dimension: asGrouping(table.dimension), sort: table.sort },
              }}
              fileParts={[
                props.client.name,
                'Google Analytics 4',
                data.table?.range.from ?? '',
                data.table?.range.to ?? '',
              ]}
              dimensionLabel={dimensionLabel(asGrouping(table.dimension))}
              metrics={cards}
              result={data.table}
            />
            <button type="button" className="btn secondary" onClick={() => setPresenting(true)}>
              Presentation Mode
            </button>
          </div>
        )}
        {presenting && (
          <button type="button" className="btn secondary" onClick={() => setPresenting(false)}>
            Exit presentation
          </button>
        )}
      </header>
      {!presenting && (
        <FilterChips
          filters={filters}
          dimensionLabel={dimensionLabel}
          onFilters={setFilters}
          interval={interval}
          onInterval={setDateInterval}
        />
      )}
      {data.error && (
        <p className="ex-error" role="alert">
          {data.error}
        </p>
      )}
      {data.cards?.state === 'ERROR' && (
        <p className="ex-error" role="alert">
          {data.cards.error}
        </p>
      )}
      {selectedMetric && (
        <ExploreCharts
          metric={selectedMetric}
          cards={data.cards}
          donut={data.donut}
          donutLabel={view.donut ? dimensionLabel(asGrouping(view.donut)) : null}
        />
      )}
      <ExploreCards
        metrics={cards}
        result={data.cards}
        selected={selected}
        onSelect={(key) => setSelectedByView({ ...selectedByView, [view.slug]: key })}
      />
      <ExploreTable
        dimensions={view.table.map((key) => ({ key, label: dimensionLabel(asGrouping(key)) }))}
        metrics={cards}
        result={data.table}
        state={table}
        onChange={(next) => setTableByView({ ...tableByView, [view.slug]: next })}
      />
      {!presenting && (
        <p className="ex-timezone">
          Dates are shown in {timezone}.{' '}
          <a
            href={hrefFor({
              area: 'client',
              clientId: props.client.id,
              tab: 'settings',
              documentId: null,
            })}
          >
            Change
          </a>
        </p>
      )}
      {settingsOpen && (
        <ExploreSettings
          metrics={allMetrics}
          enabled={metricsState.enabled}
          onToggle={metricsState.toggle}
          onSetMany={metricsState.setMany}
          property={props.property}
          manageHref={hrefFor({
            area: 'client',
            clientId: props.client.id,
            tab: 'data',
            documentId: null,
          })}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </div>
  );
  return (
    <ChangeFormatContext.Provider value={changeFormatOf(compare)}>
      {page}
    </ChangeFormatContext.Provider>
  );
}
